import { lazy, Suspense, type ReactNode, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { AuthProvider } from '@/lib/auth/context';
import { AuthGuard, PublicOnlyGuard } from '@/lib/auth/guard';
import { ToastProvider } from '@/volt/components';
import { LoadingSpinner } from '@/volt/components';

// Lazy import with auto-retry on chunk load failure (handles deploys while user has old tab open)
function lazyWithRetry(importFn: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    importFn().catch(() => {
      // Chunk failed to load (new deploy invalidated old chunks) — reload once
      const reloaded = sessionStorage.getItem('chunk_reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return { default: () => null } as any;
      }
      sessionStorage.removeItem('chunk_reload');
      return importFn(); // retry once more, let it fail naturally if still broken
    })
  );
}

// Suspense wrapper using VOLT LoadingSpinner
function SuspenseWrapper({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-screen bg-[var(--volt-dark)]">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

// Root layout: provides router-dependent context (AuthProvider, ToastProvider)
// These providers need to be inside the router because they use useNavigate
function RootLayout() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Outlet />
      </ToastProvider>
    </AuthProvider>
  );
}

// Lazy-loaded page components
const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage'));
const RegisterPage = lazyWithRetry(() => import('@/pages/RegisterPage'));
const VerifyPage = lazyWithRetry(() => import('@/pages/VerifyPage'));
const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage'));
const ChatPage = lazyWithRetry(() => import('@/pages/ChatPage'));
const ProjectsPage = lazyWithRetry(() => import('@/pages/ProjectsPage'));
const ProjectDetailPage = lazyWithRetry(() => import('@/pages/ProjectDetailPage'));
const SkillsPage = lazyWithRetry(() => import('@/pages/SkillsPage'));
const SkillDetailPage = lazyWithRetry(() => import('@/pages/SkillDetailPage'));
const SkillBuilderPage = lazyWithRetry(() => import('@/pages/SkillBuilderPage'));
const SkillTracePage = lazyWithRetry(() => import('@/pages/SkillTracePage'));
const SkillExecutionPage = lazyWithRetry(() => import('@/pages/SkillExecutionPage'));
const AgentsPage = lazyWithRetry(() => import('@/pages/AgentsPage'));
const WorkflowsPage = lazyWithRetry(() => import('@/pages/WorkflowsPage'));
const WorkflowBuilderPage = lazyWithRetry(() => import('@/pages/WorkflowBuilderPage'));
const ExecutionDetailPage = lazyWithRetry(() => import('@/pages/ExecutionDetailPage'));
const TemplatesPage = lazyWithRetry(() => import('@/pages/TemplatesPage'));
const OnboardingPage = lazyWithRetry(() => import('@/pages/OnboardingPage'));
const ConnectorsPage = lazyWithRetry(() => import('@/pages/ConnectorsPage'));
const MarketplacePage = lazyWithRetry(() => import('@/pages/MarketplacePage'));
const SettingsPage = lazyWithRetry(() => import('@/pages/SettingsPage'));

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public routes (wrapped with PublicOnlyGuard to redirect authenticated users)
      {
        path: '/login',
        element: (
          <PublicOnlyGuard>
            <SuspenseWrapper>
              <LoginPage />
            </SuspenseWrapper>
          </PublicOnlyGuard>
        ),
      },
      {
        path: '/register',
        element: (
          <PublicOnlyGuard>
            <SuspenseWrapper>
              <RegisterPage />
            </SuspenseWrapper>
          </PublicOnlyGuard>
        ),
      },
      {
        // Verify page is NOT wrapped in PublicOnlyGuard -- users may click
        // a magic link while already logged in (e.g., switching accounts)
        path: '/auth/verify',
        element: (
          <SuspenseWrapper>
            <VerifyPage />
          </SuspenseWrapper>
        ),
      },

      // Protected routes (wrapped with AuthGuard)
      {
        path: '/dashboard',
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <DashboardPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'chat',
            element: (
              <SuspenseWrapper>
                <ChatPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'projects',
            element: (
              <SuspenseWrapper>
                <ProjectsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'projects/:id',
            element: (
              <SuspenseWrapper>
                <ProjectDetailPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'skills',
            element: (
              <SuspenseWrapper>
                <SkillsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'skills/:id',
            element: (
              <SuspenseWrapper>
                <SkillDetailPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'skills/:skillId/execute',
            element: (
              <SuspenseWrapper>
                <SkillExecutionPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'skills/builder',
            element: (
              <SuspenseWrapper>
                <SkillBuilderPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'skills/traces',
            element: (
              <SuspenseWrapper>
                <SkillTracePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'skills/traces/:executionId',
            element: (
              <SuspenseWrapper>
                <SkillTracePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'agents',
            element: (
              <SuspenseWrapper>
                <AgentsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'workflows/:workflowId/executions/:executionId',
            element: (
              <SuspenseWrapper>
                <ExecutionDetailPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'workflows/builder',
            element: (
              <SuspenseWrapper>
                <WorkflowBuilderPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'workflows/builder/:id',
            element: (
              <SuspenseWrapper>
                <WorkflowBuilderPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'workflows',
            element: (
              <SuspenseWrapper>
                <WorkflowsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'templates',
            element: (
              <SuspenseWrapper>
                <TemplatesPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'onboarding',
            element: (
              <SuspenseWrapper>
                <OnboardingPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'connectors',
            element: (
              <SuspenseWrapper>
                <ConnectorsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'marketplace',
            element: (
              <SuspenseWrapper>
                <MarketplacePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'settings',
            element: (
              <SuspenseWrapper>
                <SettingsPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },

      // Root redirect
      {
        path: '/',
        element: <Navigate to="/login" replace />,
      },

      // Catch-all redirect
      {
        path: '*',
        element: <Navigate to="/login" replace />,
      },
    ],
  },
]);
